import { z } from 'zod'

/**
 * Sorting is a **closed enum per endpoint** (§5.3), never a free-text column
 * name — an open sort parameter is an injection surface and, worse here, an
 * information leak.
 *
 * BR §16 leak 2: *sorting leaks ranking*. A Staff account cannot see a
 * break-even, but `?sort=-breakEven` would hand it the same information in
 * order form — the most expensive animal is simply first. So the values that
 * rank by money are collected here, and the API rejects any of them for a
 * Staff account or a price-locked session with `403 FORBIDDEN` /
 * `403 PRICE_LOCKED`.
 *
 * They are a **set membership test**, not a string match on `-`, so a new
 * money sort added to one endpoint's enum is a one-line addition here rather
 * than a leak nobody noticed.
 */
export const MONEY_SORT_VALUES = [
  '-breakEven',
  'breakEven',
  '-costBasis',
  'costBasis',
  '-profitPerDay',
  'profitPerDay',
  '-profit',
  'profit',
  '-purchasePrice',
  'purchasePrice',
  '-salePrice',
  'salePrice',
  '-amount',
  'amount',
  '-outstanding',
  'outstanding',
  '-bookValue',
  'bookValue',
  '-pays',
  'pays',
] as const

export type MoneySortValue = (typeof MONEY_SORT_VALUES)[number]

/** The membership set the API middleware checks. Read-only on purpose. */
export const moneySortValues: ReadonlySet<string> = new Set<string>(MONEY_SORT_VALUES)

/** `if (isMoneySortValue(query.sort) && !canSeeMoney(role)) throw forbidden()`. */
export const isMoneySortValue = (value: string | undefined): value is MoneySortValue =>
  value !== undefined && moneySortValues.has(value)

/** Sort orders that are safe for every role — kept beside the gated set. */
export const sortSchema = <const T extends readonly [string, ...string[]]>(
  values: T,
  fallback: T[number],
) => z.enum(values).default(fallback)
